import { useState, useEffect } from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { SwipeableDrawer } from '@savgroup-front-common/core/src/atoms/SwipeableDrawer';
import {
  DIRECTION,
  DRAWER_SIZE,
} from '@savgroup-front-common/core/src/atoms/SwipeableDrawer/SwipeableDrawer';
import { MenuHamburgerCloseIcon } from '@savgroup-front-common/core/src/protons/icons/Menu';
import { ReversioIcon } from '@savgroup-front-common/core/src/protons/icons';
import { useInventoryThumbZone } from '../../context/InventoryThumbZoneContext';
import { useInventoryViewMode } from '../../context/InventoryViewModeContext';
import SidebarMenu from './SidebarMenu';

const HeaderContainer = styled.header`
  background-color: #002e74;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 100;
  width: 100%;
  height: 48px;
  min-height: 48px;
`;

const LeftSection = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  min-width: 0;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  flex-shrink: 0;
`;

const HeaderViewModeSwitch = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.15rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.1);
`;

const HeaderViewModeButton = styled.button<{ $isActive: boolean }>`
  border: none;
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  font-size: 0.6875rem;
  line-height: 1.2;
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 500)};
  cursor: pointer;
  color: ${({ $isActive }) => ($isActive ? '#002e74' : '#fff')};
  background: ${({ $isActive }) => ($isActive ? '#fff' : 'transparent')};
  white-space: nowrap;

  &:hover {
    background: ${({ $isActive }) => ($isActive ? '#fff' : 'rgba(255, 255, 255, 0.15)')};
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const HeaderThumbZonesButton = styled.button`
  display: none;

  @media (max-width: 719px) {
    display: inline-flex;
  }
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.55rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  max-width: min(11rem, 42vw);
  text-align: center;
  white-space: normal;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const MenuHamburger = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  min-width: 32px;
  min-height: 32px;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const HeaderLogo = styled.div`
  display: flex;
  align-items: center;
  
  a {
    text-decoration: none;
    color: inherit;
    display: flex;
    align-items: center;
  }
`;

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-sizing: border-box;
  padding: 0 1rem;
  height: 48px;
  min-height: 48px;
  border-bottom: 1px solid ${({ theme }) => theme.newUI.variationColors?.light3 || '#e0e0e0'};
  background-color: #002e74;
`;

const SidebarHeaderLogo = styled.div`
  display: flex;
  align-items: center;
  
  a {
    text-decoration: none;
    color: inherit;
    display: flex;
    align-items: center;
  }
`;

const SidebarCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-left: auto;
  
  &:hover {
    opacity: 0.8;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const theme = useTheme();
  const location = useLocation();
  const iconColor = theme?.colors?.white || '#fff';
  const inventoryDetailMatch = useMatch('/inventaire/:reference');
  const { isThumbZoneOverlayVisible, toggleThumbZoneOverlay } = useInventoryThumbZone();
  const { viewMode, setViewMode, isViewModeSwitchVisible } = useInventoryViewMode();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <>
      <HeaderContainer>
        <LeftSection>
          <MenuHamburger
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            type="button"
            aria-label="Ouvrir le menu"
          >
            <MenuHamburgerCloseIcon color={iconColor} />
          </MenuHamburger>

          <HeaderLogo>
            <Link to="/dossier" style={{ display: 'flex', alignItems: 'center' }}>
              <ReversioIcon color="#fff" />
            </Link>
          </HeaderLogo>
        </LeftSection>

        {inventoryDetailMatch ? (
          <RightSection>
            {isViewModeSwitchVisible ? (
              <HeaderViewModeSwitch role="group" aria-label="Vue inventaire">
                <HeaderViewModeButton
                  type="button"
                  $isActive={viewMode === 'superviseur'}
                  aria-pressed={viewMode === 'superviseur'}
                  onClick={() => setViewMode('superviseur')}
                >
                  Superviseur
                </HeaderViewModeButton>
                <HeaderViewModeButton
                  type="button"
                  $isActive={viewMode === 'inventoriste'}
                  aria-pressed={viewMode === 'inventoriste'}
                  onClick={() => setViewMode('inventoriste')}
                >
                  Inventoriste
                </HeaderViewModeButton>
              </HeaderViewModeSwitch>
            ) : null}
            <HeaderThumbZonesButton
              type="button"
              aria-pressed={isThumbZoneOverlayVisible}
              aria-label={
                isThumbZoneOverlayVisible
                  ? 'Masquer le calque des zones ergonomiques pouce'
                  : 'Afficher le calque des zones ergonomiques pouce sur la fiche inventaire'
              }
              title={isThumbZoneOverlayVisible ? 'Masquer zones pouce' : 'Zones pouce'}
              onClick={toggleThumbZoneOverlay}
            >
              {isThumbZoneOverlayVisible ? 'Masquer zones pouce' : 'Zones pouce'}
            </HeaderThumbZonesButton>
          </RightSection>
        ) : null}
      </HeaderContainer>

      <SwipeableDrawer
        hasCloseButton={false}
        hasPadding={false}
        isOpen={isMenuOpen}
        direction={DIRECTION.RIGHT}
        size={DRAWER_SIZE.X_SMALL}
        onClose={() => setIsMenuOpen(false)}
      >
        <SidebarContainer>
          <SidebarHeader>
            <SidebarHeaderLogo>
              <Link to="/dossier" onClick={() => setIsMenuOpen(false)}>
                <ReversioIcon color="#fff" />
              </Link>
            </SidebarHeaderLogo>
            <SidebarCloseButton
              onClick={() => setIsMenuOpen(false)}
              type={BUTTON_TYPES.BUTTON}
              aria-label="Fermer le menu"
            >
              <MenuHamburgerCloseIcon color={iconColor} />
            </SidebarCloseButton>
          </SidebarHeader>
          <SidebarMenu onItemClick={() => setIsMenuOpen(false)} />
        </SidebarContainer>
      </SwipeableDrawer>
    </>
  );
};

export default Header;
